import { useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DynamicSchemaForm } from '@/components/runner/DynamicSchemaForm';
import { useCreateEndpointTest } from '@/hooks/useEndpointTests';
import { useCreateTestCriteria } from '@/hooks/useEndpointTestEvals';
import { CriteriaDraftEditor } from './CriteriaEditor';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import { convertFormValues, getDefaultValues, isSchemaRenderable } from '@/utils/schemaUtils';
import type { Endpoint } from '@/types';
import type { CreateEndpointTestCriterionInput } from '@/types/endpointTests';

interface AddTestDialogProps {
  endpoint: Endpoint;
  onClose: () => void;
}

type InputMode = 'form' | 'json';

const fieldClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500';

/** Drop the empty strings the form starts with, so a blank field is not sent as "". */
function compactInput(values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined && value !== ''));
}

/**
 * "Add test" (task 1175): the blank form the tests list lacked. The input is
 * built schema-first with the same field builder the runner uses
 * (`DynamicSchemaForm`), with a Raw JSON tab as the fallback — and the only
 * mode when the service's input schema cannot render as a form. Creates with
 * `source: 'manual'` through the existing create hook.
 */
export function AddTestDialog({ endpoint, onClose }: AddTestDialogProps) {
  const schema = endpoint.input as Record<string, unknown> | null;
  const formAvailable = !!schema && isSchemaRenderable(schema);
  const initialValues = useMemo(() => (schema ? getDefaultValues(schema) : {}), [schema]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expectedOutcome, setExpectedOutcome] = useState('');
  const [mode, setMode] = useState<InputMode>(formAvailable ? 'form' : 'json');
  const [formValues, setFormValues] = useState<Record<string, unknown>>(initialValues);
  const [jsonText, setJsonText] = useState(JSON.stringify(compactInput(initialValues), null, 2));
  const [inputError, setInputError] = useState<string | undefined>();
  const [criteria, setCriteria] = useState<CreateEndpointTestCriterionInput[]>([]);
  const createTest = useCreateEndpointTest();
  const createCriteria = useCreateTestCriteria();
  const toast = useToast();
  // Per-scenario criteria are part of the criteria feature set (task 1215).
  const criteriaEnabled = useFlag('innovate.fe.testing.criteria');

  /** The input as it would be saved, or null with an error shown. */
  const currentInput = (): Record<string, unknown> | null => {
    if (mode === 'form') return compactInput(convertFormValues(formValues, schema));
    try {
      const parsed = JSON.parse(jsonText) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setInputError('Input must be a JSON object');
        return null;
      }
      setInputError(undefined);
      return parsed as Record<string, unknown>;
    } catch {
      setInputError('Invalid JSON format');
      return null;
    }
  };

  const switchMode = (next: InputMode) => {
    if (next === mode) return;
    if (next === 'json') {
      setJsonText(JSON.stringify(compactInput(convertFormValues(formValues, schema)), null, 2));
      setInputError(undefined);
      setMode('json');
      return;
    }
    const parsed = currentInput();
    if (!parsed) return;
    setFormValues({ ...initialValues, ...parsed });
    setMode('form');
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const input = currentInput();
    if (!input) return;
    try {
      const created = await createTest.mutateAsync({
        endpointId: endpoint.id,
        name: trimmed,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(expectedOutcome.trim() ? { expectedOutcome: expectedOutcome.trim() } : {}),
        input,
        source: 'manual',
      });
      // Criteria belong to the test, so they are created once it exists (task 1180).
      if (criteriaEnabled && criteria.length > 0) {
        try {
          await createCriteria.mutateAsync({ endpointTestId: created.id, criteria });
        } catch (err) {
          toast.error('Scenario added, but its criteria were not saved', err instanceof Error ? err.message : undefined);
          onClose();
          return;
        }
      }
      toast.success('Scenario added', trimmed);
      onClose();
    } catch (err) {
      toast.error('Failed to add scenario', err instanceof Error ? err.message : undefined);
    }
  };

  const tabClass = (active: boolean) =>
    `px-2.5 py-1 text-xs font-medium rounded-md ${
      active
        ? 'bg-indigo-600 text-white'
        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
    }`;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-test-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 id="add-test-title" className="text-lg font-medium text-gray-900 dark:text-white">
            Add scenario
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="add-test-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              id="add-test-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Refund — annual plan, day 1"
              className={fieldClass}
            />
          </div>
          <div>
            <label htmlFor="add-test-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="add-test-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={fieldClass}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Input</span>
              {formAvailable && (
                <div className="flex gap-1" role="tablist" aria-label="Input mode">
                  <button type="button" role="tab" aria-selected={mode === 'form'} onClick={() => switchMode('form')} className={tabClass(mode === 'form')}>
                    Form
                  </button>
                  <button type="button" role="tab" aria-selected={mode === 'json'} onClick={() => switchMode('json')} className={tabClass(mode === 'json')}>
                    Raw JSON
                  </button>
                </div>
              )}
            </div>
            {mode === 'form' && schema ? (
              <DynamicSchemaForm schema={schema} values={formValues} onChange={setFormValues} />
            ) : (
              <textarea
                id="add-test-input"
                aria-label="Input (JSON)"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                rows={6}
                className={`${fieldClass} font-mono text-xs`}
              />
            )}
            {inputError && <p className="mt-1 text-xs text-red-500">{inputError}</p>}
          </div>

          <div>
            <label htmlFor="add-test-expected" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Expected outcome <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="add-test-expected"
              value={expectedOutcome}
              onChange={(e) => setExpectedOutcome(e.target.value)}
              rows={2}
              className={fieldClass}
            />
          </div>

          {criteriaEnabled && (
            <div>
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Criteria for this scenario
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                Checks are deterministic; graded criteria are a rubric sentence the grader judges.
              </p>
              <CriteriaDraftEditor drafts={criteria} onChange={setCriteria} />
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={createTest.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!name.trim() || createTest.isPending || createCriteria.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createTest.isPending ? 'Saving…' : 'Save scenario'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddTestDialog;
